import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  useEngagementTheme,
  engagementPageSx,
  EngagementCard,
  EngagementPill,
  engagementButtonSx,
} from "../components/engagementTheme";

const HEALTH_BAND_TONE = { risk: "crit", watch: "warn", healthy: "ok" };

const HEALTH_BAND_LABEL = {
  risk: "At risk",
  watch: "Watch band",
  healthy: "Healthy",
};

const componentPillTone = (score, max) => {
  const ratio = max > 0 ? score / max : 0;
  if (ratio < 0.5) return "crit";
  if (ratio < 0.8) return "warn";
  return "ok";
};

const componentStateLabel = (score, max) => {
  const ratio = max > 0 ? score / max : 0;
  if (ratio < 0.5) return "Low";
  if (ratio < 0.8) return "Watch";
  return "Good";
};

const SUBSCRIPTION_STATUS_TONE = { active: "ok", upcoming: "info", expired: "crit", paused: "mute" };
const STATUS_LABEL = { active: "Active", upcoming: "Upcoming", expired: "Expired", paused: "Paused" };
const TERM_LABEL = { monthly: "Monthly", yearly: "Yearly" };
const INSTALLMENT_LABEL = { monthly: "monthly", quarterly: "quarterly", "bi-annual": "bi-annually" };

const DUE_BUCKET_TONE = { overdue: "crit", today: "warn", week: "mute" };
const DUE_BUCKET_LABEL = { overdue: "Overdue", today: "Due today", week: "Due this week" };

const CALL_OUTCOMES = ["Spoke", "Did not answer", "Call back later", "Wrong number"];
const TABS = ["Timeline", "Calls", "Renewal", "Tickets", "Health score"];
const ASSIGN_CATEGORIES = ["Renewal", "Calls", "Training", "Feedback", "Camp", "Influencer", "Upgrade", "Onboarding", "Support"];
const TIMELINE_PAGE_SIZE = 10;

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart}, ${timePart}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const ONBOARDING_STATUS_LABEL = { pending: "Pending", complete: "Done", "activation pending": "Activation pending" };

const onboardingCallText = (call) => {
  if (!call) return "Not offered";
  if (call.selectedSlot) return `${call.confirmed ? "Confirmed" : "Booked"} for ${formatDateTime(call.selectedSlot)}`;
  if (call.offerFrom && call.offerTo) return `Offered ${formatDate(call.offerFrom)} to ${formatDate(call.offerTo)}, waiting for the doctor`;
  return "Offered, waiting for the doctor";
};

const renewalText = (data) => {
  if (data.daysLeft === null || data.daysLeft === undefined) return "No subscription on record";
  if (data.daysLeft < 0) return `Ended ${Math.abs(data.daysLeft)} day${Math.abs(data.daysLeft) === 1 ? "" : "s"} ago, ${formatDate(data.subscriptionEnd)}`;
  return `Due ${formatDate(data.subscriptionEnd)}, in ${data.daysLeft} day${data.daysLeft === 1 ? "" : "s"}`;
};

const renewedBeforeText = (history) => {
  const renewals = Math.max(0, (history || []).length - 1);
  if (!(history || []).length) return "No subscription on record";
  return renewals === 0 ? "Not renewed yet" : `Renewed ${renewals} time${renewals === 1 ? "" : "s"}`;
};

const inaugurationOutcomeText = (inauguration) => {
  if (!inauguration) return "Not recorded";
  const outcome = inauguration.outcome ? inauguration.outcome.charAt(0).toUpperCase() + inauguration.outcome.slice(1) : "";
  if (!outcome) return inauguration.date ? "Not completed yet" : "Not recorded";
  return inauguration.notes ? `${outcome}. ${inauguration.notes}` : outcome;
};

const formatAmount = (amount, currency) => (amount ? `${currency === "INR" || !currency ? "₹" : currency + " "}${Number(amount).toLocaleString("en-IN")}` : "—");

const PAYMENT_STATUS_TONE = { paid: "ok", initiated: "warn", partially_paid: "warn", rejected: "crit" };

const daysAgo = (value) => {
  if (!value) return null;
  const diff = Math.floor((Date.now() - new Date(value).getTime()) / (24 * 60 * 60 * 1000));
  return diff;
};

const renewTone = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return "mute";
  if (daysLeft < 0) return "crit";
  if (daysLeft <= 90) return "warn";
  return "ok";
};

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

// datetime-local wants "YYYY-MM-DDTHH:mm" in local time, not an ISO string.
const toDatetimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const isWebLink = (value) => /^https?:\/\/\S+$/i.test(value || "");

const clinicFieldText = (field, value) => {
  if (field.type === "select") return (field.options || []).find((o) => o.value === value)?.label || "—";
  if (field.type === "date") return value ? formatDate(value) : "—";
  if (field.type === "number") return value === "" || value === null ? "—" : `₹${Number(value).toLocaleString("en-IN")}`;
  return value || "—";
};

const EngagementClinicDetail = () => {
  const t = useEngagementTheme();
  const { doctorId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("Timeline");
  const [calls, setCalls] = useState([]);
  const [callsPage, setCallsPage] = useState(1);
  const [callsTotalPages, setCallsTotalPages] = useState(1);
  const [callsLoading, setCallsLoading] = useState(false);
  const [ticketsList, setTicketsList] = useState([]);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotalPages, setTicketsTotalPages] = useState(1);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryPage, setPaymentHistoryPage] = useState(1);
  const [paymentHistoryTotalPages, setPaymentHistoryTotalPages] = useState(1);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [collectingId, setCollectingId] = useState("");
  const [timelinePage, setTimelinePage] = useState(1);
  const [callOpen, setCallOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleValue, setRescheduleValue] = useState("");
  const [ticketOpen, setTicketOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackScore, setFeedbackScore] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackVia, setFeedbackVia] = useState("call");
  const [outcome, setOutcome] = useState("Spoke");
  const [notes, setNotes] = useState("");
  const [callActionItems, setCallActionItems] = useState("");
  const [callNextValue, setCallNextValue] = useState("");
  const [ticketCategory, setTicketCategory] = useState("general");
  const [ticketPriority, setTicketPriority] = useState("medium");
  const [ticketSaid, setTicketSaid] = useState("");
  const [saving, setSaving] = useState(false);
  const [officers, setOfficers] = useState([]);
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCategory, setAssignCategory] = useState("Calls");
  const [assignTo, setAssignTo] = useState("me");
  const [assignDue, setAssignDue] = useState("");
  const [assignWhat, setAssignWhat] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editCountryCode, setEditCountryCode] = useState("");
  const [editClinicName, setEditClinicName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editQualification, setEditQualification] = useState("");
  const [editFrontDeskName, setEditFrontDeskName] = useState("");
  const [editFrontDeskPhone, setEditFrontDeskPhone] = useState("");
  const [editWhatsappGroup, setEditWhatsappGroup] = useState("");
  const [editTier, setEditTier] = useState("standard");
  const [editDemo, setEditDemo] = useState({});
  const [editRenewal, setEditRenewal] = useState({});
  const [editInfluencer, setEditInfluencer] = useState({});
  const [taskReassignTo, setTaskReassignTo] = useState({});
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const API = `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement`;
  const headers = { Authorization: `Bearer ${auth}` };

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/officers`, { headers })
      .then((res) => {
        if (!cancelled) setOfficers(res.data.officers || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${API}/clinics/${doctorId}`, { headers });
      setData(res.data.data);
    } catch (error) {
      console.error("Clinic detail fetch failed:", error);
      const message = error.response?.data?.message || error.message || "Error while fetching this clinic";
      setLoadError(message);
      Toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setTimelinePage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  const loadCalls = async (page) => {
    setCallsLoading(true);
    try {
      const res = await axios.get(`${API}/clinics/${doctorId}/calls`, { headers, params: { page, limit: 10 } });
      setCalls(res.data.calls || []);
      setCallsTotalPages(Math.max(1, res.data.totalPages || 1));
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load calls.");
    } finally {
      setCallsLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "Calls") return;
    loadCalls(callsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, callsPage, doctorId]);

  const loadTickets = async (page) => {
    setTicketsLoading(true);
    try {
      const res = await axios.get(`${API}/clinics/${doctorId}/tickets`, { headers, params: { page, limit: 10 } });
      setTicketsList(res.data.tickets || []);
      setTicketsTotalPages(Math.max(1, res.data.totalPages || 1));
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load tickets.");
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "Tickets") return;
    loadTickets(ticketsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, ticketsPage, doctorId]);

  const loadPaymentHistory = async (page) => {
    setPaymentHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/renewals/payments`, { headers, params: { doctorId, page, limit: 10 } });
      setPaymentHistory(res.data.data || []);
      setPaymentHistoryTotalPages(Math.max(1, res.data.totalPages || 1));
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load payment history.");
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "Renewal") return;
    loadPaymentHistory(paymentHistoryPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, paymentHistoryPage, doctorId]);

  const collectInstallment = async (paymentId) => {
    setCollectingId(paymentId);
    try {
      await axios.post(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/subscription/pay-installment`, { paymentId }, { headers });
      Toast.success("Installment collected.");
      loadPaymentHistory(paymentHistoryPage);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not collect this installment.");
    } finally {
      setCollectingId("");
    }
  };

  useEffect(() => {
    if (!data) return;
    setEditName(data.doctorName || "");
    setEditEmail(data.email || "");
    setEditMobile(data.mobile || "");
    setEditCountryCode(data.countryCode || "");
    setEditClinicName(data.clinicName || "");
    setEditAddress(data.address || "");
    setEditCity(data.city || "");
    setEditState(data.state || "");
    setEditQualification(data.qualification || "");
    setEditFrontDeskName(data.frontDeskName || "");
    setEditFrontDeskPhone(data.frontDeskPhone || "");
    setEditWhatsappGroup(data.whatsappGroup || "");
    setEditTier(data.tier || "standard");
    setEditDemo({
      status: data.demo?.status || "",
      scheduledAt: toDateInput(data.demo?.scheduledAt),
      doneAt: toDateInput(data.demo?.doneAt),
      planChosen: data.demo?.planChosen || "",
    });
    setEditRenewal({ status: data.renewal?.status || "", pendingReason: data.renewal?.pendingReason || "" });
    setEditInfluencer({
      name: data.influencer?.name || "",
      phone: data.influencer?.phone || "",
      instagramUrl: data.influencer?.instagramUrl || "",
      rawVideosUrl: data.influencer?.rawVideosUrl || "",
      shootDate: toDateInput(data.influencer?.shootDate),
      shootStatus: data.influencer?.shootStatus || "",
      charges: data.influencer?.charges ?? "",
      paymentStatus: data.influencer?.paymentStatus || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const openCallPanel = () => {
    setCallNextValue(toDatetimeLocal(data?.nextCallAt));
    setCallOpen((o) => !o);
  };

  const assignTask = async () => {
    if (!assignWhat.trim()) return;
    setSaving(true);
    try {
      const assignToId = assignTo === "me" ? undefined : assignTo;
      await axios.post(
        `${API}/my-day/tasks`,
        { doctorId, category: assignCategory, action: assignWhat.trim(), dueDate: assignDue || undefined, assignToId },
        { headers }
      );
      Toast.success(assignTo === "me" ? "Task added to your My Day." : "Assigned. It's in their My Day now.");
      setAssignOpen(false);
      setAssignWhat("");
      setAssignDue("");
      setAssignCategory("Calls");
      setAssignTo("me");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not assign this task.");
    } finally {
      setSaving(false);
    }
  };

  const completeOpenTask = async (taskId) => {
    try {
      await axios.patch(`${API}/my-day/tasks/${taskId}/complete`, { outcome: "Done" }, { headers });
      Toast.success("Task completed.");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not complete this task.");
    }
  };

  const reassignOpenTask = async (taskId) => {
    const newOwnerId = taskReassignTo[taskId];
    if (!newOwnerId) return;
    try {
      await axios.patch(`${API}/my-day/tasks/${taskId}/reassign`, { newOwnerId }, { headers });
      Toast.success("Task moved.");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not move this task.");
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/clinics/${doctorId}/profile`,
        {
          qualification: editQualification,
          frontDeskName: editFrontDeskName,
          frontDeskPhone: editFrontDeskPhone,
          whatsappGroup: editWhatsappGroup,
          tier: editTier,
          demo: editDemo,
          renewal: editRenewal,
          influencer: editInfluencer,
        },
        { headers }
      );
      Toast.success("Saved to the master database.");
      setEditOpen(false);
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not save these details.");
    } finally {
      setSaving(false);
    }
  };

  const addRenewalNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try {
      const res = await axios.post(`${API}/clinics/${doctorId}/notes`, { text: noteDraft }, { headers });
      setData((prev) => ({ ...prev, notes: res.data.data }));
      setNoteDraft("");
      setNoteOpen(false);
      Toast.success("Note added.");
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not add this note.");
    } finally {
      setSavingNote(false);
    }
  };

  const changeOwner = async () => {
    if (!newOwnerId) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/assignments/${doctorId}`, { ownerId: newOwnerId }, { headers });
      Toast.success("Clinic reassigned.");
      setChangeOwnerOpen(false);
      setNewOwnerId("");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not reassign this clinic.");
    } finally {
      setSaving(false);
    }
  };

  const saveCall = async () => {
    setSaving(true);
    try {
      const nextSteps = {};
      const actionItems = callActionItems.split("\n").map((line) => line.trim()).filter(Boolean);
      if (actionItems.length) {
        nextSteps.actionItems = actionItems;
      }
      if (callNextValue) {
        nextSteps.scheduleFollowUp = new Date(callNextValue).toISOString();
      }
      await axios.post(
        `${API}/clinics/${doctorId}/calls`,
        { outcome, notes, nextSteps: Object.keys(nextSteps).length ? nextSteps : undefined },
        { headers }
      );
      Toast.success(actionItems.length ? "Call saved. Action items became tasks." : "Call saved.");
      setCallOpen(false);
      setNotes("");
      setOutcome("Spoke");
      setCallActionItems("");
      setCallNextValue("");
      load();
      setCallsPage(1);
      if (tab === "Calls") loadCalls(1);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not log this call.");
    } finally {
      setSaving(false);
    }
  };

  const saveReschedule = async () => {
    if (!rescheduleValue) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/clinics/${doctorId}/next-call`, { nextCallAt: new Date(rescheduleValue).toISOString() }, { headers });
      Toast.success("Next call rescheduled.");
      setRescheduleOpen(false);
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not reschedule.");
    } finally {
      setSaving(false);
    }
  };

  const saveTicket = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/clinics/${doctorId}/tickets`,
        { subject: ticketSaid.trim(), category: ticketCategory, priority: ticketPriority },
        { headers }
      );
      Toast.success(`Ticket ${res.data.data.ticketId} created in the support dashboard.`);
      setTicketOpen(false);
      setTicketCategory("general");
      setTicketPriority("medium");
      setTicketSaid("");
      setTicketsPage(1);
      setTab("Tickets");
      load();
      if (tab === "Tickets") loadTickets(1);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not raise this ticket.");
    } finally {
      setSaving(false);
    }
  };

  const saveFeedback = async () => {
    if (feedbackScore === null) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/clinics/${doctorId}/feedback`,
        { score: feedbackScore, comment: feedbackComment, collectedVia: feedbackVia },
        { headers }
      );
      Toast.success("Feedback logged.");
      setFeedbackOpen(false);
      setFeedbackScore(null);
      setFeedbackComment("");
      setFeedbackVia("call");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not log this feedback.");
    } finally {
      setSaving(false);
    }
  };

  const markPromiseKept = async (promiseId) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/clinics/${doctorId}/promises/${promiseId}/kept`, {}, { headers });
      Toast.success("Promise marked as kept.");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not update this promise.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ ...engagementPageSx(t), position: "relative", minHeight: 300 }}>
        <PageLoader />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={engagementPageSx(t)}>
        <Typography component={Link} to="/dashboard/engagement/clinics" sx={{ fontWeight: 600, color: t.accent, textDecoration: "none" }}>
          {"←"} Back to Clinics
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: t.txtTone.crit }}>Couldn't load this clinic</Typography>
        <Typography sx={{ fontSize: 14, color: t.textMuted }}>{loadError || "Unknown error."}</Typography>
        <Box component="button" type="button" onClick={load} sx={{ ...engagementButtonSx(t), alignSelf: "flex-start" }}>
          Retry
        </Box>
      </Box>
    );
  }

  const openPromises = data.promises.filter((p) => p.status === "open" || p.status === "overdue");
  const callActionTasks = data.openTasks.filter((t) => t.category === "Calls");
  const lastCall = data.calls[0] || null;
  const discussedLines = lastCall?.notes ? lastCall.notes.split("\n").map((l) => l.trim()).filter(Boolean) : [];
  const timelineEvents = [
    ...data.calls.map((c) => ({ kind: "call", at: c.at, call: c })),
    ...(data.activeCampCard ? [{ kind: "camp", at: data.activeCampCard.updatedAt || data.activatedAt, card: data.activeCampCard }] : []),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  const timelineTotalPages = Math.max(1, Math.ceil(timelineEvents.length / TIMELINE_PAGE_SIZE));
  const pagedTimelineEvents = timelineEvents.slice((timelinePage - 1) * TIMELINE_PAGE_SIZE, timelinePage * TIMELINE_PAGE_SIZE);

  const clinicFieldGroups = [
    {
      state: editDemo,
      setState: setEditDemo,
      fields: [
        { key: "status", label: "Demo", type: "select", options: data.options?.demoStatus },
        { key: "scheduledAt", label: "Demo scheduled on", type: "date" },
        { key: "doneAt", label: "Demo done on", type: "date" },
        { key: "planChosen", label: "Plan chosen at demo", type: "text" },
      ],
    },
    {
      state: editRenewal,
      setState: setEditRenewal,
      fields: [
        { key: "status", label: "Renewal status", type: "select", options: data.options?.renewalStatus },
        { key: "pendingReason", label: "Pending reason", type: "text" },
      ],
    },
    {
      state: editInfluencer,
      setState: setEditInfluencer,
      fields: [
        { key: "name", label: "Shoot influencer", type: "text" },
        { key: "phone", label: "Influencer phone", type: "text" },
        { key: "instagramUrl", label: "Instagram link", type: "link" },
        { key: "rawVideosUrl", label: "Raw videos link", type: "link" },
        { key: "shootDate", label: "Shoot date", type: "date" },
        { key: "shootStatus", label: "Shoot status", type: "select", options: data.options?.shootStatus },
        { key: "charges", label: "Shoot charges", type: "number" },
        { key: "paymentStatus", label: "Shoot payment", type: "select", options: data.options?.shootPayment },
      ],
    },
  ];

  const billingTerm = [
    data.term ? TERM_LABEL[data.term] || data.term : null,
    data.installmentType ? `billed ${INSTALLMENT_LABEL[data.installmentType] || data.installmentType}` : null,
    data.preferredPaymentMethod ? `usually pays by ${data.preferredPaymentMethod}` : null,
  ]
    .filter(Boolean)
    .join(", ") || "—";

  return (
    <Box sx={engagementPageSx(t)}>
      <Box component="nav" aria-label="Breadcrumb" sx={{ fontSize: 14, color: t.textMuted, display: "flex", gap: "8px" }}>
        <Typography component={Link} to="/dashboard/engagement/clinics" sx={{ fontWeight: 600, color: t.accent, textDecoration: "none" }}>
          Clinics
        </Typography>
        <span aria-hidden="true">/</span>
        <Typography component="span">{data.doctorName}, {data.clinicName}</Typography>
      </Box>

      {/* Header */}
      <EngagementCard t={t}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "flex-start" }}>
          <Box
            aria-hidden="true"
            sx={{ width: 64, height: 64, borderRadius: "14px", bgcolor: t.tone.ok.bg, color: t.tone.ok.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, flex: "none" }}
          >
            {initials(data.doctorName)}
          </Box>
          <Box sx={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em" }}>{data.doctorName}</Typography>
            <Typography sx={{ fontSize: 16, color: t.textSecondary }}>
              {[data.clinicName, data.city, data.state].filter(Boolean).join(", ") || "—"}
            </Typography>
            {data.phone && (
              <Typography component="a" href={`tel:${data.phone}`} sx={{ fontSize: 16, fontWeight: 600, textDecoration: "none", color: t.accent }}>{data.phone}</Typography>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "4px" }}>
              {data.subscriptionStatus && (
                <EngagementPill t={t} tone={SUBSCRIPTION_STATUS_TONE[data.subscriptionStatus] || "mute"}>{STATUS_LABEL[data.subscriptionStatus] || data.subscriptionStatus}</EngagementPill>
              )}
              <EngagementPill t={t} tone={renewTone(data.daysLeft)}>
                {data.subscriptionEnd ? `Subscription valid till ${formatDate(data.subscriptionEnd)}` : "No active subscription"}
              </EngagementPill>
              {data.plan && <EngagementPill t={t} tone="info">{data.plan}{data.term ? `, ${TERM_LABEL[data.term] || data.term} term` : ""}</EngagementPill>}
              <EngagementPill t={t} tone="mute">{data.device || "Device not recorded"}</EngagementPill>
              <EngagementPill t={t} tone={HEALTH_BAND_TONE[data.health.band] || "mute"}>
                Health {data.health.total}, {data.health.band}
              </EngagementPill>
              <EngagementPill t={t} tone="mute">Owner: {data.owner?.name || "Unassigned"}</EngagementPill>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <Box component="button" type="button" onClick={openCallPanel} sx={engagementButtonSx(t, "solid")}>
              {callOpen ? "Close" : "Log call"}
            </Box>
            <Box component="button" type="button" onClick={() => setAssignOpen((o) => !o)} sx={engagementButtonSx(t)}>
              {assignOpen ? "Close" : "Assign task"}
            </Box>
            <Box component="button" type="button" onClick={() => setTicketOpen((o) => !o)} sx={engagementButtonSx(t)}>
              {ticketOpen ? "Close" : "Raise ticket"}
            </Box>
            <Box component="button" type="button" onClick={() => { setChangeOwnerOpen((o) => !o); setNewOwnerId(""); }} sx={engagementButtonSx(t)}>
              {changeOwnerOpen ? "Close" : "Change officer"}
            </Box>
            <Box component={Link} to={`/dashboard/engagement/camps?doctorId=${doctorId}`} sx={{ ...engagementButtonSx(t), textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              Plan a camp
            </Box>
          </Box>
        </Box>

        {changeOwnerOpen && (
          <Box sx={{ padding: "18px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "14px" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Change the engagement officer for {data.doctorName}. Currently: {data.owner?.name || "Unassigned"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
              <Select
                size="small"
                displayEmpty
                value={newOwnerId}
                onChange={(e) => setNewOwnerId(e.target.value)}
                sx={{ height: 44, minWidth: 220, bgcolor: t.inputBg }}
              >
                <MenuItem value="" disabled>Choose an officer</MenuItem>
                {officers.filter((o) => o._id !== data.owner?.id).map((o) => (
                  <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                ))}
              </Select>
              <Box component="button" type="button" disabled={saving || !newOwnerId} onClick={changeOwner} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !newOwnerId ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Reassign"}
              </Box>
            </Box>
          </Box>
        )}

        {assignOpen && (
          <Box sx={{ padding: "18px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "14px" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Assign a task for {data.doctorName}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
              <Select size="small" value={assignCategory} onChange={(e) => setAssignCategory(e.target.value)} sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}>
                {ASSIGN_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
              <Select size="small" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}>
                <MenuItem value="me">Me</MenuItem>
                {officers.map((o) => (
                  <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                ))}
              </Select>
              <TextField label="Due" type="date" InputLabelProps={{ shrink: true }} value={assignDue} onChange={(e) => setAssignDue(e.target.value)} sx={{ bgcolor: t.inputBg }} />
            </Box>
            <TextField label="What needs doing" value={assignWhat} onChange={(e) => setAssignWhat(e.target.value)} sx={{ bgcolor: t.inputBg }} />
            <Box component="button" type="button" disabled={saving || !assignWhat.trim()} onClick={assignTask} sx={{ ...engagementButtonSx(t, "solid"), alignSelf: "flex-start", opacity: saving || !assignWhat.trim() ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Assign"}
            </Box>
          </Box>
        )}

        {callOpen && (
          <Box sx={{ padding: "18px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "14px" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Log a call with {data.doctorName}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {CALL_OUTCOMES.map((o) => (
                <Box
                  key={o}
                  component="button"
                  type="button"
                  onClick={() => setOutcome(o)}
                  sx={{
                    minHeight: 44, padding: "0 14px", border: "1px solid",
                    borderColor: outcome === o ? t.selectedBg : t.disabledBorder,
                    borderRadius: "8px", bgcolor: outcome === o ? t.selectedBg : t.cardBg,
                    color: outcome === o ? t.selectedText : t.textPrimary, font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {o}
                </Box>
              ))}
            </Box>
            <TextField multiline minRows={2} label="What was discussed" value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ bgcolor: t.inputBg }} />
            <TextField
              multiline
              minRows={2}
              label="Action items, one per line"
              value={callActionItems}
              onChange={(e) => setCallActionItems(e.target.value)}
              sx={{ bgcolor: t.inputBg }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
              <TextField
                label="Next call"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={callNextValue}
                onChange={(e) => setCallNextValue(e.target.value)}
                sx={{ bgcolor: t.inputBg }}
              />
              <Box component="button" type="button" disabled={saving} onClick={saveCall} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Save call"}
              </Box>
              <Box component="button" type="button" onClick={() => setCallOpen(false)} sx={{ minHeight: 44, padding: "0 14px", border: 0, bgcolor: "transparent", color: t.textSecondary, font: "inherit", fontSize: 14, cursor: "pointer" }}>
                Cancel
              </Box>
            </Box>
          </Box>
        )}

        {ticketOpen && (
          <Box sx={{ padding: "18px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <Select size="small" value={ticketCategory} onChange={(e) => setTicketCategory(e.target.value)} sx={{ height: 56, minWidth: 140, bgcolor: t.inputBg }}>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            <Select size="small" value={ticketPriority} onChange={(e) => setTicketPriority(e.target.value)} sx={{ height: 56, minWidth: 120, bgcolor: t.inputBg }}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
            <TextField label="What the clinic said" value={ticketSaid} onChange={(e) => setTicketSaid(e.target.value)} sx={{ bgcolor: t.inputBg, minWidth: 260, flex: "1 1 260px" }} />
            <Box component="button" type="button" disabled={saving || !ticketSaid.trim()} onClick={saveTicket} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !ticketSaid.trim() ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Create in support dashboard"}
            </Box>
          </Box>
        )}
      </EngagementCard>

      {/* Open tasks */}
      <EngagementCard t={t}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Open tasks for {data.doctorName}</Typography>
          <Typography sx={{ fontSize: 14, color: t.textMuted }}>{data.openTasks.length} open</Typography>
        </Box>
        {data.openTasks.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No open tasks for this doctor.</Typography>}
        {data.openTasks.map((task) => (
          <Box key={task.id} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", py: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
            <EngagementPill t={t} tone="info">{task.category}</EngagementPill>
            <Box sx={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{task.action}</Typography>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{task.note || (task.assignedToName ? `Assigned to ${task.assignedToName}` : "")}</Typography>
            </Box>
            <EngagementPill t={t} tone={DUE_BUCKET_TONE[task.dueBucket] || "mute"}>{DUE_BUCKET_LABEL[task.dueBucket] || task.dueBucket}</EngagementPill>
            <Box component="button" type="button" onClick={() => completeOpenTask(task.id)} sx={{ minHeight: 44, padding: "0 14px", border: `1px solid ${t.accent}`, borderRadius: "8px", bgcolor: t.cardBg, color: t.accent, font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              Complete
            </Box>
            <Select
              size="small"
              displayEmpty
              value={taskReassignTo[task.id] || ""}
              onChange={(e) => {
                setTaskReassignTo((prev) => ({ ...prev, [task.id]: e.target.value }));
              }}
              sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}
            >
              <MenuItem value="">Move to…</MenuItem>
              {officers.filter((o) => o._id !== task.assignedToId).map((o) => (
                <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
              ))}
            </Select>
            <Box
              component="button"
              type="button"
              disabled={!taskReassignTo[task.id]}
              onClick={() => reassignOpenTask(task.id)}
              sx={{ minHeight: 44, padding: "0 14px", border: `1px solid ${t.disabledBorder}`, borderRadius: "8px", bgcolor: t.cardBg, color: t.textPrimary, font: "inherit", fontSize: 14, fontWeight: 600, cursor: taskReassignTo[task.id] ? "pointer" : "not-allowed", opacity: taskReassignTo[task.id] ? 1 : 0.6 }}
            >
              Move
            </Box>
          </Box>
        ))}
      </EngagementCard>

      {/* Next call / Last call */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "stretch" }}>
        <EngagementCard t={t} sx={{ flex: "1 1 280px", minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Next call</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{data.nextCallAt ? formatDateTime(data.nextCallAt) : "Not scheduled"}</Typography>
          <Typography sx={{ fontSize: 14, color: t.textSecondary }}>With {data.doctorName}.</Typography>
          {rescheduleOpen && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-end", padding: "12px", bgcolor: t.hoverBg, borderRadius: "10px" }}>
              <TextField
                label="New time"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={rescheduleValue}
                onChange={(e) => setRescheduleValue(e.target.value)}
                sx={{ bgcolor: t.inputBg }}
              />
              <Box component="button" type="button" disabled={saving} onClick={saveReschedule} sx={{ ...engagementButtonSx(t), opacity: saving ? 0.7 : 1 }}>
                Save
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "auto", pt: "6px" }}>
            <Box component="button" type="button" onClick={() => setRescheduleOpen((o) => !o)} sx={engagementButtonSx(t)}>
              {rescheduleOpen ? "Cancel" : "Reschedule"}
            </Box>
          </Box>
        </EngagementCard>

        <EngagementCard t={t} sx={{ flex: "999 1 520px", minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Last call</Typography>
            {lastCall && (
              <Typography sx={{ fontSize: 14, color: t.textMuted }}>
                {formatDate(lastCall.at)}. {lastCall.by || "Unknown officer"} with {data.doctorName}. Outcome: {lastCall.outcome}.
              </Typography>
            )}
          </Box>
          {lastCall ? (
            <>
              {discussedLines.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Discussed</Typography>
                  <Box component="ul" sx={{ margin: 0, pl: "20px", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {discussedLines.map((line, i) => (
                      <Typography component="li" key={i} sx={{ fontSize: 14, color: t.textSecondary }}>{line}</Typography>
                    ))}
                  </Box>
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Action items</Typography>
                {callActionTasks.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>None open.</Typography>}
                {callActionTasks.map((task) => (
                  <Box key={task.id} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", padding: "10px 12px", border: `1px solid ${t.borderMuted}`, borderRadius: "8px" }}>
                    <Typography sx={{ flex: "1 1 240px", minWidth: 0, fontSize: 14, fontWeight: 600 }}>{task.action}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted, width: 150 }}>{task.assignedToName || "—"}</Typography>
                    <EngagementPill t={t} tone={task.dueBucket === "overdue" ? "crit" : "warn"}>{task.dueBucket === "overdue" ? "Overdue" : task.dueBucket === "today" ? "Due today" : "Due this week"}</EngagementPill>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: t.textMuted }}>No calls logged yet.</Typography>
          )}
        </EngagementCard>
      </Box>

      {/* Clinic details */}
      <EngagementCard t={t}>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Clinic details</Typography>
          <Box sx={{ display: "flex", gap: "8px" }}>
            {editOpen && (
              <Box component="button" type="button" disabled={saving} onClick={saveProfile} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Save to master database"}
              </Box>
            )}
            <Box component="button" type="button" onClick={() => setEditOpen((o) => !o)} sx={engagementButtonSx(t)}>
              {editOpen ? "Cancel" : "Edit details"}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
          {[
            ["Doctor name", editName],
            ["Clinic name", editClinicName],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{label}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value || "—"}</Typography>
            </Box>
          ))}

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>Phone</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{[editCountryCode, editMobile].filter(Boolean).join(" ") || "—"}</Typography>
          </Box>

          {[
            ["Email", editEmail],
            ["Address", editAddress],
            ["City", editCity],
            ["State", editState],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{label}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value || "—"}</Typography>
            </Box>
          ))}

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {editOpen ? (
              <TextField label="Qualification" value={editQualification} onChange={(e) => setEditQualification(e.target.value)} sx={{ bgcolor: t.inputBg }} />
            ) : (
              <>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Qualification</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{editQualification || "—"}</Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {editOpen ? (
              <Box sx={{ display: "flex", gap: "8px" }}>
                <TextField label="Front desk name" value={editFrontDeskName} onChange={(e) => setEditFrontDeskName(e.target.value)} sx={{ bgcolor: t.inputBg, flex: 1 }} />
                <TextField label="Front desk phone" value={editFrontDeskPhone} onChange={(e) => setEditFrontDeskPhone(e.target.value)} sx={{ bgcolor: t.inputBg, flex: 1 }} />
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Front desk contact</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{[editFrontDeskName, editFrontDeskPhone].filter(Boolean).join(", ") || "—"}</Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>WhatsApp group created</Typography>
            {editOpen ? (
              <Select size="small" displayEmpty value={editWhatsappGroup} onChange={(e) => setEditWhatsappGroup(e.target.value)} sx={{ height: 44, bgcolor: t.inputBg }}>
                <MenuItem value="">Not set</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            ) : (
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{editWhatsappGroup === "yes" ? "Yes" : editWhatsappGroup === "no" ? "No" : "—"}</Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {editOpen ? (
              <Select size="small" value={editTier} onChange={(e) => setEditTier(e.target.value)} sx={{ height: 44, bgcolor: t.inputBg }}>
                <MenuItem value="high">High-touch (contact every 7 days)</MenuItem>
                <MenuItem value="standard">Standard (contact every 30 days)</MenuItem>
                <MenuItem value="self">Self-serve (contact every 45 days)</MenuItem>
              </Select>
            ) : (
              <>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Contact tier</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                  {editTier === "high" ? "High-touch (7 days)" : editTier === "self" ? "Self-serve (45 days)" : "Standard (30 days)"}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", pt: "14px", borderTop: `1px solid ${t.borderMuted}` }}>
          {[
            ["Plan", data.plan || "—"],
            ["Billing term", billingTerm],
            ["Device, from Orders", data.device || "Not recorded"],
            ["Activated on, from Orders", data.activatedAt ? formatDate(data.activatedAt) : "Not activated yet"],
            ["Subscription valid till", data.subscriptionEnd ? formatDate(data.subscriptionEnd) : "—"],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{label}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", pt: "14px", borderTop: `1px solid ${t.borderMuted}` }}>
          {[
            ["Onboarding", ONBOARDING_STATUS_LABEL[data.onboardingStatus] || "Not recorded"],
            ["Onboarding call, from Orders", onboardingCallText(data.onboardingCall)],
            ["Inauguration, from Orders", data.inauguration?.date ? `${formatDate(data.inauguration.date)}${data.inauguration.slot ? `, ${data.inauguration.slot}` : ""}` : "Not scheduled"],
            ["Influencer", data.inauguration?.influencer || "Not recorded"],
            ["Inauguration outcome", inaugurationOutcomeText(data.inauguration)],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{label}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", pt: "14px", borderTop: `1px solid ${t.borderMuted}` }}>
          {[
            ["Renewal", renewalText(data)],
            ["Renewal history", renewedBeforeText(data.subscriptionHistory)],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{label}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value}</Typography>
            </Box>
          ))}
          <Box sx={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>Renewal notes</Typography>
            {(data.notes || []).length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No notes yet.</Typography>}
            {(data.notes || []).map((n, i) => (
              <Box key={i} sx={{ padding: "6px 8px", bgcolor: t.hoverBg, borderRadius: "6px" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>{n.by}, {formatDate(n.createdAt)}</Typography>
                <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{n.text}</Typography>
              </Box>
            ))}
            {noteOpen ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", padding: "8px", bgcolor: t.hoverBg, borderRadius: "8px" }}>
                <TextField size="small" multiline minRows={2} label="New note" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} sx={{ bgcolor: t.inputBg }} />
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Box
                    component="button"
                    type="button"
                    disabled={savingNote || !noteDraft.trim()}
                    onClick={addRenewalNote}
                    sx={{ ...engagementButtonSx(t, "solid"), opacity: savingNote || !noteDraft.trim() ? 0.6 : 1 }}
                  >
                    {savingNote ? "Adding…" : "Add"}
                  </Box>
                  <Box component="button" type="button" onClick={() => { setNoteOpen(false); setNoteDraft(""); }} sx={{ ...engagementButtonSx(t), border: 0 }}>
                    Close
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                component="button"
                type="button"
                onClick={() => setNoteOpen(true)}
                sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary, alignSelf: "flex-start" }}
              >
                Add note
              </Box>
            )}
          </Box>
        </Box>

        {clinicFieldGroups.map((group, groupIndex) => (
          <Box key={groupIndex} sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", pt: "14px", borderTop: `1px solid ${t.borderMuted}` }}>
            {group.fields.map((field) => {
              const value = group.state[field.key] ?? "";
              const setValue = (next) => group.setState((prev) => ({ ...prev, [field.key]: next }));
              return (
                <Box key={field.key} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>{field.label}</Typography>
                  {editOpen && field.type === "select" && (
                    <Select size="small" displayEmpty value={value} onChange={(e) => setValue(e.target.value)} sx={{ height: 44, bgcolor: t.inputBg }}>
                      <MenuItem value="">Not set</MenuItem>
                      {(field.options || []).map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  )}
                  {editOpen && field.type !== "select" && (
                    <TextField
                      size="small"
                      type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
                      inputProps={field.type === "number" ? { min: 0 } : undefined}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      sx={{ bgcolor: t.inputBg }}
                    />
                  )}
                  {!editOpen && field.type === "link" && isWebLink(value) && (
                    <Typography component="a" href={value} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 15, fontWeight: 600, color: t.textPrimary, textDecoration: "underline", wordBreak: "break-all" }}>
                      Open link
                    </Typography>
                  )}
                  {!editOpen && !(field.type === "link" && isWebLink(value)) && (
                    <Typography sx={{ fontSize: 15, fontWeight: 600, wordBreak: "break-word" }}>{clinicFieldText(field, value)}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </EngagementCard>

      {/* History */}
      <EngagementCard t={t} sx={{ padding: 0, gap: 0 }}>
        <Box role="tablist" aria-label="Clinic sections" sx={{ display: "flex", gap: "4px", overflowX: "auto", padding: "0 12px", borderBottom: `1px solid ${t.border}` }}>
          {TABS.map((tabName) => (
            <Box
              key={tabName}
              component="button"
              type="button"
              role="tab"
              aria-selected={tab === tabName}
              onClick={() => setTab(tabName)}
              sx={{
                minHeight: 48, padding: "0 12px", border: 0, borderBottom: "3px solid", borderBottomColor: tab === tabName ? t.accent : "transparent",
                bgcolor: "transparent", font: "inherit", fontSize: 15, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                color: tab === tabName ? t.textPrimary : t.textMuted,
              }}
            >
              {tabName}
            </Box>
          ))}
        </Box>

        <Box sx={{ padding: "16px 20px 20px" }}>
          {tab === "Timeline" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Box component="ol" sx={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
              {timelineEvents.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No activity logged yet.</Typography>}
              {pagedTimelineEvents.map((e) => {
                if (e.kind === "call") {
                  return (
                    <Box component="li" key={`call-${e.call.id}`} sx={{ display: "flex", gap: "14px", py: "14px", borderBottom: `1px solid ${t.borderMuted}` }}>
                      <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: "5px", mt: "6px", flex: "none", bgcolor: e.call.outcome === "Spoke" ? t.tone.ok.color : t.disabledText }} />
                      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "8px" }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{e.call.outcome}{e.call.by ? ` — ${e.call.by}` : ""}</Typography>
                          <Typography sx={{ fontSize: 13, color: t.textMuted }}>{formatDateTime(e.call.at)}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, color: t.textMuted }}>Call</Typography>
                        {e.call.notes && <Typography sx={{ fontSize: 14, color: t.textSecondary }}>{e.call.notes}</Typography>}
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Box component="li" key="camp" sx={{ display: "flex", gap: "14px", py: "14px", borderBottom: `1px solid ${t.borderMuted}` }}>
                    <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: "5px", mt: "6px", flex: "none", bgcolor: t.lane.ini.color }} />
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "8px" }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Camp partner search: {e.card.partnerDescription}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>{e.card.stageName}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", pt: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {timelinePage} of {timelineTotalPages}</Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  disabled={timelinePage <= 1}
                  onClick={() => setTimelinePage((p) => Math.max(1, p - 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: timelinePage <= 1 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={timelinePage >= timelineTotalPages}
                  onClick={() => setTimelinePage((p) => Math.min(timelineTotalPages, p + 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: timelinePage >= timelineTotalPages ? "not-allowed" : "pointer" }}
                >
                  Next
                </Box>
              </Box>
            </Box>
            </Box>
          )}
          {tab === "Calls" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {!callsLoading && calls.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No calls logged yet.</Typography>}
              {calls.map((c) => (
                <Box key={c.id} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "10px", py: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1 1 260px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{c.outcome}{c.by ? ` — ${c.by}` : ""}</Typography>
                    {c.notes && <Typography sx={{ fontSize: 14, color: t.textSecondary }}>{c.notes}</Typography>}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>{formatDateTime(c.at)}</Typography>
                </Box>
              ))}
              {(
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", pt: "4px" }}>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {callsPage} of {callsTotalPages}</Typography>
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Box
                      component="button"
                      type="button"
                      disabled={callsPage <= 1}
                      onClick={() => setCallsPage((p) => Math.max(1, p - 1))}
                      sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: callsPage <= 1 ? "not-allowed" : "pointer" }}
                    >
                      Previous
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      disabled={callsPage >= callsTotalPages}
                      onClick={() => setCallsPage((p) => Math.min(callsTotalPages, p + 1))}
                      sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: callsPage >= callsTotalPages ? "not-allowed" : "pointer" }}
                    >
                      Next
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {tab === "Renewal" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <Box component="dl" sx={{ margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px" }}>
                <Box><Typography component="dt" sx={{ fontSize: 13, color: t.textMuted }}>Software subscription</Typography><Typography component="dd" sx={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{data.subscriptionEnd ? `Valid till ${formatDate(data.subscriptionEnd)}` : "—"}</Typography></Box>
                <Box><Typography component="dt" sx={{ fontSize: 13, color: t.textMuted }}>Plan</Typography><Typography component="dd" sx={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{data.plan || "—"}</Typography></Box>
                <Box><Typography component="dt" sx={{ fontSize: 13, color: t.textMuted }}>Owner</Typography><Typography component="dd" sx={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{data.owner?.name || "Unassigned"}</Typography></Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "10px" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Renewal readiness</Typography>
                <EngagementPill t={t} tone={openPromises.length > 0 ? "warn" : "ok"}>{openPromises.length > 0 ? `${openPromises.length} open promise${openPromises.length === 1 ? "" : "s"}` : "None open"}</EngagementPill>
              </Box>
              {openPromises.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No open promises for this clinic.</Typography>}
              {openPromises.map((p) => (
                <Box key={p.id} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", py: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1 1 260px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{p.what}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>Due {formatDate(p.dueDate)}. By {p.owner || "—"}.</Typography>
                  </Box>
                  <EngagementPill t={t} tone={p.status === "overdue" ? "crit" : "warn"}>{p.status === "overdue" ? "Overdue" : "Open"}</EngagementPill>
                  <Box component="button" type="button" disabled={saving} onClick={() => markPromiseKept(p.id)} sx={{ ...engagementButtonSx(t), opacity: saving ? 0.6 : 1 }}>
                    Mark as kept
                  </Box>
                </Box>
              ))}
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Value report, ROI review and plan-comparison tracking aren't built yet.</Typography>

              <Typography sx={{ fontSize: 15, fontWeight: 700, mt: "8px" }}>Subscription history</Typography>
              {(!data.subscriptionHistory || data.subscriptionHistory.length === 0) && (
                <Typography sx={{ fontSize: 14, color: t.textMuted }}>No subscriptions on record for this doctor.</Typography>
              )}
              {(data.subscriptionHistory || []).map((s) => (
                <Box key={s.id} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", py: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1 1 240px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{s.plan || "—"}{s.billingCycle ? `, ${TERM_LABEL[s.billingCycle] || s.billingCycle}` : ""}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{formatDate(s.startDate)} to {formatDate(s.endDate)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatAmount(s.price, s.currency)}</Typography>
                  <EngagementPill t={t} tone={SUBSCRIPTION_STATUS_TONE[s.status] || "mute"}>{STATUS_LABEL[s.status] || s.status}</EngagementPill>
                </Box>
              ))}

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "10px", mt: "8px" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Payment history</Typography>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Recorded via Engagement only</Typography>
              </Box>
              {!paymentHistoryLoading && paymentHistory.length === 0 && (
                <Typography sx={{ fontSize: 14, color: t.textMuted }}>No payments recorded via Engagement for this doctor yet.</Typography>
              )}
              {paymentHistory.map((p) => (
                <Box key={p.id} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", py: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1 1 240px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{formatAmount(p.amount, p.currency)}, {p.method}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                      {p.period || "Period not recorded"}. Ref {p.reference || "Not given"}. Entered by {p.enteredBy || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                    <EngagementPill t={t} tone={PAYMENT_STATUS_TONE[p.status] || "mute"}>{p.status}</EngagementPill>
                    {p.isInstallment && p.installments?.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "4px" }}>
                        {p.installments.map((inst) => (
                          <EngagementPill
                            key={inst.installmentNo}
                            t={t}
                            tone={inst.status === "paid" ? "ok" : "warn"}
                            sx={{ fontSize: 11 }}
                          >
                            #{inst.installmentNo}: {formatAmount(inst.amount, p.currency)}{inst.status === "paid" && inst.paidAt ? ` (${formatDate(inst.paidAt)})` : ""}
                          </EngagementPill>
                        ))}
                      </Box>
                    )}
                    {p.isInstallment && p.status === "partially_paid" && (
                      <Box
                        component="button"
                        type="button"
                        disabled={collectingId === p.id}
                        onClick={() => collectInstallment(p.id)}
                        sx={{ ...engagementButtonSx(t), minHeight: 32, padding: "0 10px", fontSize: 12, opacity: collectingId === p.id ? 0.6 : 1 }}
                      >
                        {collectingId === p.id ? "Collecting…" : `Collect next (${formatAmount(p.nextInstallmentAmount, p.currency)})`}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
              {paymentHistory.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", pt: "4px" }}>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {paymentHistoryPage} of {paymentHistoryTotalPages}</Typography>
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Box
                      component="button"
                      type="button"
                      disabled={paymentHistoryPage <= 1}
                      onClick={() => setPaymentHistoryPage((p) => Math.max(1, p - 1))}
                      sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: paymentHistoryPage <= 1 ? "not-allowed" : "pointer" }}
                    >
                      Previous
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      disabled={paymentHistoryPage >= paymentHistoryTotalPages}
                      onClick={() => setPaymentHistoryPage((p) => Math.min(paymentHistoryTotalPages, p + 1))}
                      sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: paymentHistoryPage >= paymentHistoryTotalPages ? "not-allowed" : "pointer" }}
                    >
                      Next
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {tab === "Tickets" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {!ticketsLoading && ticketsList.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No tickets raised for this clinic yet.</Typography>}
              {ticketsList.map((ticket) => (
                <Box key={ticket._id} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "10px", py: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1 1 260px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{ticket.ticketId}: {ticket.subject}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{ticket.category} {"•"} {ticket.priority} priority {"•"} {formatDate(ticket.createdAt)}</Typography>
                  </Box>
                  <EngagementPill t={t} tone={ticket.status === "closed" ? "ok" : ticket.status === "in-progress" ? "warn" : "info"}>{ticket.status}</EngagementPill>
                </Box>
              ))}
              {(
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", pt: "4px" }}>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {ticketsPage} of {ticketsTotalPages}</Typography>
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Box
                      component="button"
                      type="button"
                      disabled={ticketsPage <= 1}
                      onClick={() => setTicketsPage((p) => Math.max(1, p - 1))}
                      sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: ticketsPage <= 1 ? "not-allowed" : "pointer" }}
                    >
                      Previous
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      disabled={ticketsPage >= ticketsTotalPages}
                      onClick={() => setTicketsPage((p) => Math.min(ticketsTotalPages, p + 1))}
                      sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: ticketsPage >= ticketsTotalPages ? "not-allowed" : "pointer" }}
                    >
                      Next
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {tab === "Health score" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "10px" }}>
                <Typography sx={{ fontSize: 26, fontWeight: 700, color: t.tone[HEALTH_BAND_TONE[data.health.band] || "mute"].color }}>
                  {data.health.total}<Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: t.textMuted }}> / {data.health.max}, {HEALTH_BAND_LABEL[data.health.band]}</Typography>
                </Typography>
                <Box component="button" type="button" onClick={() => setFeedbackOpen((o) => !o)} sx={engagementButtonSx(t)}>
                  {feedbackOpen ? "Close" : "Log feedback"}
                </Box>
              </Box>

              {feedbackOpen && (
                <Box sx={{ padding: "18px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "14px" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>How likely is {data.doctorName} to recommend scanO (0 to 10)?</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {Array.from({ length: 11 }, (_, n) => n).map((n) => (
                      <Box
                        key={n}
                        component="button"
                        type="button"
                        onClick={() => setFeedbackScore(n)}
                        sx={{
                          width: 38, height: 38, borderRadius: "8px", border: "1px solid",
                          borderColor: feedbackScore === n ? t.textPrimary : t.disabledBorder,
                          bgcolor: feedbackScore === n ? t.selectedBg : t.cardBg,
                          color: feedbackScore === n ? t.selectedText : t.textPrimary,
                          font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        {n}
                      </Box>
                    ))}
                  </Box>
                  <Select size="small" value={feedbackVia} onChange={(e) => setFeedbackVia(e.target.value)} sx={{ height: 44, maxWidth: 200, bgcolor: t.inputBg }}>
                    <MenuItem value="call">Collected on a call</MenuItem>
                    <MenuItem value="whatsapp">Collected over WhatsApp</MenuItem>
                    <MenuItem value="in_person">Collected in person</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  <TextField multiline minRows={2} placeholder="Comment (optional)" value={feedbackComment} onChange={(e) => setFeedbackComment(e.target.value)} sx={{ bgcolor: t.inputBg }} />
                  <Box
                    component="button"
                    type="button"
                    disabled={saving || feedbackScore === null}
                    onClick={saveFeedback}
                    sx={{ ...engagementButtonSx(t, "solid"), alignSelf: "flex-start", opacity: saving || feedbackScore === null ? 0.6 : 1 }}
                  >
                    {saving ? "Saving…" : "Save feedback"}
                  </Box>
                </Box>
              )}

              {data.health.components.map((c) => (
                <Box key={c.key} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "10px", py: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontSize: 14, opacity: c.tracked ? 1 : 0.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1 1 260px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{c.label} {c.tracked ? `${c.score} of ${c.max}` : `not tracked / ${c.max}`}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{c.detail}</Typography>
                  </Box>
                  {c.tracked && <EngagementPill t={t} tone={componentPillTone(c.score, c.max)}>{componentStateLabel(c.score, c.max)}</EngagementPill>}
                </Box>
              ))}

              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Weekly scans</Typography>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Last {data.scanUsage.weekly.length} weeks</Typography>
                {(() => {
                  const max = Math.max(1, ...data.scanUsage.weekly.map((w) => w.count));
                  return (
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px", height: 120 }}>
                      {data.scanUsage.weekly.map((w) => (
                        <Box key={w.weekStart} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", height: "100%", justifyContent: "flex-end" }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{w.count}</Typography>
                          <Box sx={{ width: "100%", height: `${Math.max(4, (w.count / max) * 90)}px`, bgcolor: w.count / max < 0.5 ? t.tone.crit.color : w.count / max < 0.8 ? t.tone.warn.color : t.tone.ok.color, borderRadius: "3px" }} />
                          <Typography sx={{ fontSize: 11, color: t.textMuted }}>{new Date(w.weekStart).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
        </Box>
      </EngagementCard>

    </Box>
  );
};

export default EngagementClinicDetail;
